import { CURRENCY_API_CODE } from '@/types/modifiers/references';
import { useConfig } from 'context/Config';
import { format, formatDistanceToNow, intlFormat } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import useUserApi from 'hooks/UserApi/useUserApi';
import { useTranslation } from 'next-i18next';
import { SHORT_LOCALE_OPTIONS } from './IntlFormatOptions';

export const useDateFnsWithLocale = () => {
  const {
    i18n: { language },
  } = useTranslation();

  const locale = language === 'fr-fr' ? fr : enUS;
  const formatDistanceToNowWithLocale = (date: Date) => formatDistanceToNow(date, { locale });

  const formatWithLocale = (date: Date, formatOption: string) => format(date, formatOption, { locale });

  const localeCode = language === 'fr-fr' ? fr.code : enUS.code;
  const localeFormat = (date: Date, options: Intl.DateTimeFormatOptions = SHORT_LOCALE_OPTIONS) =>
    intlFormat(date || '', options, { locale: localeCode });

  return { formatDistanceToNow: formatDistanceToNowWithLocale, localeFormat, format: formatWithLocale };
};

export const useFormatPrice = () => {
  const { user } = useUserApi();
  const currency = user?.currency;
  const {
    i18n: { language },
  } = useTranslation();
  const { preferredUserLocale } = useConfig();
  const defaultCurency = language === 'fr-fr' ? CURRENCY_API_CODE.EUR : CURRENCY_API_CODE.USD;

  const intlUSD = new Intl.NumberFormat(preferredUserLocale, {
    style: 'currency',
    currency: CURRENCY_API_CODE.USD,
  });

  const intlEUR = new Intl.NumberFormat(preferredUserLocale, {
    style: 'currency',
    currency: CURRENCY_API_CODE.EUR,
  });

  const allFormat = {
    [CURRENCY_API_CODE.EUR]: intlEUR.format,
    [CURRENCY_API_CODE.USD]: intlUSD.format,
  };

  return {
    allFormat,
    currency: currency || defaultCurency,
    formatUserCurrency: allFormat[currency || defaultCurency],
  };
};
